import { Grid, Link, makeStyles, Theme } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Stack, Typography } from "@mui/material";
import React, { FunctionComponent, useContext } from "react";
import { useTranslation } from "react-i18next";
import { FeedbackType } from "../../../api/smartmelts-api/graphql/generated";
import { AttentionIcon } from "../../../assets/icons/attention-icon";
import { ContentText } from "../../../components/text/content-text";
import { HeadingText } from "../../../components/text/heading-text";
import { MenuSubcategoryDictionary } from "../../../models/categories";
import { MenuItemCategory, MenuItemSubcategory } from "../../../models/types/category-types";
import { NutritionContext } from "../context/nutrition-context";
import { useGeneDataGroupTolerances } from "../hooks/use-tolerances";
import { QuestionSectionLoadingIndicator } from "../question/question-section-loading-indicator";
import { InfoBox } from "./components/info-box";
import { ToleranceBox } from "./components/tolerance-box";
import { AnswerContext } from "./context/answer-context";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    width: "100%",
  },
  contentText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  link: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  toleranceWrapper: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2),
    },
  },
}));

interface IGeneInfoAnswerProps {}

export const GeneInfoAnswer: FunctionComponent<IGeneInfoAnswerProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { activeQuestion } = useContext(AnswerContext);
  const { activeSubcategory, changeActiveCategories } = useContext(NutritionContext);
  const tolerances = useGeneDataGroupTolerances();
  const tolerance = tolerances[MenuSubcategoryDictionary[activeSubcategory].geneDataGroupTypes[0]];

  return (
    <Grid container className={classes.container}>
      <HeadingText>{t(`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_INFO}.heading`)}</HeadingText>
      <Grid item className={classes.contentText}>
        <ContentText light>
          {t(`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_INFO}.description_1`)}
        </ContentText>
      </Grid>
      <Grid item className={classes.contentText}>
        <ContentText light small>
          {t(`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_INFO}.description_2`)}
        </ContentText>
      </Grid>
      <Grid item container justify="flex-start" className={classes.link}>
        <Stack direction="row" spacing={2}>
          <ArrowForwardIcon color="primary" />
          <Typography color="primary">
            <Link
              variant="inherit"
              href=""
              onClick={(event: React.SyntheticEvent) => {
                event.preventDefault();
                changeActiveCategories(
                  MenuItemCategory.RECOMMENDATIONS,
                  MenuItemSubcategory.GENEVARIATIONS,
                  2,
                  MenuSubcategoryDictionary[activeSubcategory].geneDataGroupTypes[0],
                );
              }}
            >
              {t(`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_INFO}.link`)}
            </Link>
          </Typography>
        </Stack>
      </Grid>
      {!tolerances.loading && tolerance && (
        <Grid container alignItems="stretch">
          <Grid item xs={12} sm={4} md={6} lg={4} className={classes.toleranceWrapper}>
            <ToleranceBox
              icon={tolerance.icon}
              name={tolerance.name}
              labelColor={tolerance.labelColor}
              label={tolerance.label}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={6} lg={4} className={classes.toleranceWrapper}>
            <InfoBox
              icon={AttentionIcon}
              text={t(`twin_communication.${activeQuestion.question}.${FeedbackType.GENE_INFO}.warning`)}
            />
          </Grid>
        </Grid>
      )}
      {tolerances.loading && <QuestionSectionLoadingIndicator />}
    </Grid>
  );
};
